/**
 * useSupabase — Custom Hooks für Supabase-Datenabfragen
 * Zentrale Hooks für Akten, Aufgaben, Termine, Kunden, Rechnungen, Mitarbeiter,
 * Betriebe, Rechnungspositionen, Zahlungseingänge, DATEV-Exporte, Honorartabelle
 * 
 * ROBUST: Wenn Tabellen nicht existieren (PGRST205), wird ein leeres Array
 * zurückgegeben statt einer Fehlermeldung.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase, type BusinessUnit } from '@/lib/supabase';
import type { Akte, Aufgabe, Termin, Kunde, Rechnung, Mitarbeiter, BetriebDB, Rechnungsposition, Zahlungseingang, DatevExport, Honorar, Nummernkreis, LeistungsKatalogItem } from '@/lib/database.types';
import { BETRIEB_IDS, type BetriebId } from '@/lib/betriebe';
import { excludeDeleted } from './aktenFilters';
import {
  bestimmeAktenSortierung,
  normalisiereStellungnahmeFilter,
  type AktenSortierung,
} from '@/lib/akten-stellungnahme';
import type { AkteFrist } from '@/lib/database.types';
import { decideAktenErrorHandling } from '@/lib/akten-query-error';
import { Sentry } from '@/lib/sentry';
import {
  berechneAlterBucketBedingungen,
  type AktenErweiterterFilter,
} from '@/lib/akten-filter-erweiterung';

// Prüfe ob Fehler ein "Tabelle nicht gefunden" ist
function isTableNotFound(error: any): boolean {
  if (!error) return false;
  const msg = error.message || error.code || '';
  return msg.includes('PGRST205') || msg.includes('schema cache') || msg.includes('relation') || msg.includes('does not exist');
}

// Generic fetch hook mit robuster Fehlerbehandlung
function useQuery<T>(fetcher: () => Promise<T>, deps: any[] = [], fallback?: T) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      setData(result);
    } catch (err: any) {
      if (isTableNotFound(err) && fallback !== undefined) {
        setData(fallback);
        setError(null);
      } else {
        if (!isTableNotFound(err)) {
          Sentry.captureException(err, { extra: { hook: 'useQuery' } });
        }
        setError(err.message || 'Fehler beim Laden');
      }
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

// ============================================================
// AKTEN
// ============================================================
export function useAkten(businessUnit?: BusinessUnit) {
  return useQuery(async () => {
    let query = supabase
      .from('akten')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1000); // Safety cap — paginated lists should use useAktenPaginated.

    query = excludeDeleted(query);

    if (businessUnit) {
      query = query.eq('business_unit', businessUnit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as Akte[];
  }, [businessUnit], [] as any[]);
}

/**
 * Server-side paginated akten hook with debounced search + filters.
 *
 * Design:
 *   - Range-based pagination (`.range(from, to)`) instead of loading everything.
 *   - Search runs as Postgres `or(...)` with `ilike` across the usual key fields.
 *     We wait 300ms after each keystroke before firing a new query.
 *   - Status + Auftragsart filters are server-side equality checks.
 *   - `total` comes from a separate HEAD request with `count: 'exact'` so we
 *     show the true count without streaming every row.
 *   - `loadMore` appends the next page in-place; the page state resets
 *     whenever any query input changes.
 *
 * Intended for lists like AktenPage where the underlying table has thousands
 * of rows. Dashboard / lightweight callers should stick with `useAkten`.
 */
export interface AktenSubstatusFilter {
  zahlungsstatus?: string[];   // OR innerhalb; leer = kein Filter
  klage_status?: string[];     // OR innerhalb; leer = kein Filter
  warten_auf?: string[];       // OR innerhalb; leer = kein Filter
  stellungnahme?: string[];    // OR innerhalb; leer = kein Filter
}

export interface UseAktenPaginatedOptions {
  businessUnit?: BusinessUnit;
  search?: string;           // applied via ilike on akten_nr, kunde_name, titel, kennzeichen, fahrzeug_vin, versicherung
  status?: string | null;    // akte.status equality (null/'alle' = no filter)
  auftragsart?: string | null; // matches auftragstyp_sv OR auftragsart
  /**
   * Stellungnahme-Unter-Status ("offen" | "versendet" | "abgerechnet").
   * `null` / `undefined` / "alle" = kein Filter. Wenn gesetzt, sortiert die
   * Abfrage nach `schriftsatzfrist` aufsteigend (siehe Issue #243).
   */
  stellungnahmeStatus?: string | null;
  /** Sub-Status multi-select filter (Issue #416). AND zwischen Sections, OR innerhalb. */
  substatusFilter?: AktenSubstatusFilter;
  /** Kritisch-Quickfilter (Issue #416): Schriftsatzfrist < 14 Tage ODER Klage aktiv. */
  kritischFilter?: boolean;
  /** 7 neue erweiterte Filter (Issue #653): Bezahl-Status, Stufe, Forderung offen, Alter, Frist. */
  erweiterterFilter?: AktenErweiterterFilter;
  /** Manuelle Sortierung (Issue #652). Überschreibt stellungnahme-basierte Default-Sortierung. */
  manualSort?: AktenSortierung | null;
  /** Per-Betrieb-Default-Sortierung (Issue #652). Greift wenn kein manualSort und kein Stellungnahme-Filter. */
  defaultSort?: AktenSortierung;
  pageSize?: number;         // default 50
}
export interface UseAktenPaginatedResult {
  rows: Akte[];
  total: number;             // server-reported total matching filters
  loading: boolean;          // initial page or filter-change load
  loadingMore: boolean;      // subsequent loadMore() calls
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
}

// Escape PostgREST `ilike` wildcards + separators so user input is literal.
function escapeIlike(input: string): string {
  return input.replace(/([\\%_,()])/g, '\\$1');
}

// ─── Frist-Pre-Fetch (Issue #653) ────────────────────────────────────────────
// Vor der Haupt-Akten-Query: akte_ids aus akten_fristen holen, damit die
// Haupt-Query mit .in('id', ids) oder .not('id', 'in', ids) filtern kann.
// Nutzt idx_akten_fristen_offen (akte_id, faellig_am WHERE erledigt_am IS NULL).

type FristPreFetchResult =
  | { type: 'include'; ids: string[] }
  | { type: 'exclude'; ids: string[] }
  | null;

function isoDateMinusDays(isoDate: string, days: number): string {
  const d = new Date(isoDate);
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

async function preFetchFristAkteIds(
  ef: AktenErweiterterFilter,
  today: string,
): Promise<FristPreFetchResult> {
  const { mit_frist, faellig_seit } = ef;
  if (!mit_frist?.length && !faellig_seit?.length) return null;

  const includeIds = new Set<string>();
  const noFristSelected = mit_frist?.includes('keine') ?? false;

  if (mit_frist?.length) {
    if (mit_frist.includes('aktiv')) {
      const { data } = await supabase
        .from('akten_fristen').select('akte_id').is('erledigt_am', null).gte('faellig_am', today).limit(5000);
      data?.forEach((r: { akte_id: string }) => includeIds.add(r.akte_id));
    }
    if (mit_frist.includes('ueberfaellig')) {
      const { data } = await supabase
        .from('akten_fristen').select('akte_id').is('erledigt_am', null).lt('faellig_am', today).limit(5000);
      data?.forEach((r: { akte_id: string }) => includeIds.add(r.akte_id));
    }
  }

  if (faellig_seit?.length) {
    for (const fs of faellig_seit) {
      let q = supabase.from('akten_fristen').select('akte_id').is('erledigt_am', null) as any;
      switch (fs) {
        case 'nicht_faellig':
          q = q.gte('faellig_am', today);
          break;
        case 'd0_7':
          q = q.lt('faellig_am', today).gte('faellig_am', isoDateMinusDays(today, 7));
          break;
        case 'd7_30':
          q = q.lt('faellig_am', isoDateMinusDays(today, 7)).gte('faellig_am', isoDateMinusDays(today, 30));
          break;
        case 'd30_90':
          q = q.lt('faellig_am', isoDateMinusDays(today, 30)).gte('faellig_am', isoDateMinusDays(today, 90));
          break;
        case 'gt90d':
          q = q.lt('faellig_am', isoDateMinusDays(today, 90));
          break;
      }
      const { data } = await q.limit(5000);
      (data as { akte_id: string }[] | null)?.forEach(r => includeIds.add(r.akte_id));
    }
  }

  if (noFristSelected && includeIds.size === 0) {
    // Nur 'keine' ausgewählt: alle akte_ids MIT Fristen holen → excluden
    const { data } = await supabase
      .from('akten_fristen').select('akte_id').is('erledigt_am', null).limit(5000);
    const idsWithFristen = data ? [...new Set((data as { akte_id: string }[]).map(r => r.akte_id))] : [];
    return { type: 'exclude', ids: idsWithFristen };
  }

  return includeIds.size > 0 ? { type: 'include', ids: [...includeIds] } : null;
}

function applyFristFilter(builder: any, fristResult: FristPreFetchResult): any {
  if (!fristResult) return builder;
  if (fristResult.type === 'include') return builder.in('id', fristResult.ids);
  if (fristResult.type === 'exclude' && fristResult.ids.length > 0) {
    return builder.not('id', 'in', fristResult.ids);
  }
  return builder;
}

export function useAktenPaginated(opts: UseAktenPaginatedOptions): UseAktenPaginatedResult {
  const { businessUnit, search = '', status, auftragsart, stellungnahmeStatus, substatusFilter, kritischFilter, erweiterterFilter, manualSort, defaultSort, pageSize = 50 } = opts;

  const [rows, setRows] = useState<Akte[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [nextOffset, setNextOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [debouncedSearch, setDebouncedSearch] = useState<string>(search);
  const [version, setVersion] = useState<number>(0); // bumped on refresh()

  // Stabilize object references to prevent unnecessary re-renders (Issue: Flicker-Fix).
  // React's useCallback compares deps with Object.is(), so object literals
  // (substatusFilter, erweiterterFilter) create new refs on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableSubstatusFilter = useMemo(() => substatusFilter, [JSON.stringify(substatusFilter)]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableErweiterterFilter = useMemo(() => erweiterterFilter, [JSON.stringify(erweiterterFilter)]);

  // Debounce the search term by 300ms.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Build the shared query filter (without pagination). Returns a function so
  // both the count query and the row query can reuse the same WHERE clauses.
  const applyFilters = useCallback((builder: any) => {
    builder = excludeDeleted(builder);
    if (businessUnit) builder = builder.eq('business_unit', businessUnit);
    if (status && status !== 'alle') builder = builder.eq('status', status);
    if (auftragsart && auftragsart !== 'alle') {
      builder = builder.or(`auftragstyp_sv.eq.${auftragsart},auftragsart.eq.${auftragsart}`);
    }
    const stellungnahmeFilter = normalisiereStellungnahmeFilter(stellungnahmeStatus);
    const stnSubstatusActive = stableSubstatusFilter?.stellungnahme && stableSubstatusFilter.stellungnahme.length > 0;
    if (stellungnahmeFilter && !stnSubstatusActive) {
      builder = builder.eq('stellungnahme_status', stellungnahmeFilter);
    }

    // Sub-Status-Filter (Issue #416) — AND zwischen Sections, OR innerhalb
    if (stableSubstatusFilter) {
      const { zahlungsstatus: zs, klage_status: ks, warten_auf: wa, stellungnahme: stn } = stableSubstatusFilter;
      if (zs && zs.length > 0) {
        builder = builder.in('zahlungsstatus', zs);
      }
      if (ks && ks.length > 0) {
        builder = builder.in('klage_status', ks);
      }
      if (wa && wa.length > 0) {
        builder = builder.in('warten_auf', wa);
      }
      if (stn && stn.length > 0) {
        builder = builder.in('stellungnahme_status', stn);
      }
    }

    // Kritisch-Quickfilter (Issue #416): schriftsatzfrist < 14d ODER Klage aktiv
    if (kritischFilter) {
      const in14Days = new Date();
      in14Days.setDate(in14Days.getDate() + 14);
      const iso14 = in14Days.toISOString().split('T')[0];
      builder = builder.or(
        `schriftsatzfrist.lte.${iso14},klage_status.in.(klage_erhoben,beweisaufnahme,urteil,berufung)`
      );
    }

    // Erweiterter Filter (Issue #653) — synchrone Teile
    if (stableErweiterterFilter) {
      const ef = stableErweiterterFilter;

      // Bezahl-Status: zahlungsstatus IN (...)
      if (ef.bezahl_status?.length) {
        builder = builder.in('zahlungsstatus', ef.bezahl_status);
      }

      // Forderungs-Stufe: forderung_stage IN (...)
      if (ef.forderungs_stufe?.length) {
        builder = builder.in('forderung_stage', ef.forderungs_stufe);
      }

      // Forderung offen: forderungsakte_id IS NOT NULL AND forderung_stage != 9
      if (ef.forderung_offen) {
        builder = builder
          .not('forderungsakte_id', 'is', null)
          .or('forderung_stage.is.null,forderung_stage.neq.9');
      }

      // Alter-Bucket: created_at in Datums-Range(s) — OR zwischen Buckets
      const alterRanges = berechneAlterBucketBedingungen(ef.alter_bucket);
      if (alterRanges?.length) {
        const orParts = alterRanges.map(r =>
          `and(created_at.gte.${r.from},created_at.lte.${r.to})`
        );
        builder = builder.or(orParts.join(','));
      }

      // Betragsbereich: rechnung_betrag BETWEEN betrag_min AND betrag_max
      if (typeof ef.betrag_min === 'number' && ef.betrag_min > 0) {
        builder = builder.gte('rechnung_betrag', ef.betrag_min);
      }
      if (typeof ef.betrag_max === 'number' && ef.betrag_max < 10000) {
        builder = builder.lte('rechnung_betrag', ef.betrag_max);
      }

      // Ersteller-Quelle: created_via IN (...)
      if (ef.created_via?.length) {
        builder = builder.in('created_via', ef.created_via);
      }
    }

    if (debouncedSearch) {
      const term = `%${escapeIlike(debouncedSearch)}%`;
      builder = builder.or([
        `akten_nr.ilike.${term}`,
        `akten_nummer.ilike.${term}`,
        `titel.ilike.${term}`,
        `kunde_name.ilike.${term}`,
        `kennzeichen.ilike.${term}`,
        `fahrzeug_kennzeichen.ilike.${term}`,
        `fahrzeug_vin.ilike.${term}`,
        `fin.ilike.${term}`,
        `versicherung.ilike.${term}`,
      ].join(','));
    }
    return builder;
  }, [businessUnit, status, auftragsart, stellungnahmeStatus, stableSubstatusFilter, kritischFilter, stableErweiterterFilter, debouncedSearch]);

  // Wenn der Stellungnahme-Filter aktiv ist (Einzel-Select oder Sub-Status-Filter),
  // wird nach Schriftsatzfrist aufsteigend sortiert (ablaufende Fristen zuerst).
  // manualSort (Issue #652) überschreibt diese Logik wenn gesetzt.
  // defaultSort (Issue #652) wird als per-Betrieb-Fallback verwendet, wenn weder
  // manualSort noch Stellungnahme-Filter aktiv sind.
  const sortierung = useMemo(
    () => manualSort ?? bestimmeAktenSortierung({
      stellungnahmeStatus,
      substatusStellungnahme: stableSubstatusFilter?.stellungnahme,
      defaultSort,
    }),
    [stellungnahmeStatus, stableSubstatusFilter, manualSort, defaultSort],
  );

  // Reset + load first page whenever the filter inputs change.
  useEffect(() => {
    let cancelled = false;

    async function loadFirstPage() {
      setLoading(true);
      setError(null);
      try {
        const today = new Date().toISOString().split('T')[0];
        const fristResult = stableErweiterterFilter
          ? await preFetchFristAkteIds(stableErweiterterFilter, today)
          : null;
        if (cancelled) return;

        // Count query (HEAD, no rows) gives us the true total.
        let countQ = supabase.from('akten').select('*', { count: 'exact', head: true });
        countQ = applyFilters(countQ);
        countQ = applyFristFilter(countQ, fristResult);
        const { count, error: countErr } = await countQ;
        if (cancelled) return;
        if (countErr) throw countErr;
        setTotal(count || 0);

        // First page of rows.
        let rowQ = supabase.from('akten').select('*, creator_mitarbeiter:created_by_mitarbeiter_id(vorname, nachname)')
          .order(sortierung.column, { ascending: sortierung.ascending, nullsFirst: sortierung.nullsFirst })
          .range(0, pageSize - 1);
        rowQ = applyFilters(rowQ);
        rowQ = applyFristFilter(rowQ, fristResult);
        const { data, error: rowErr } = await rowQ;
        if (cancelled) return;
        if (rowErr) throw rowErr;

        const newRows = (data || []) as Akte[];
        setRows(newRows);
        setNextOffset(newRows.length);
        setHasMore(newRows.length === pageSize && newRows.length < (count || 0));
      } catch (e: any) {
        if (cancelled) return;
        const decision = decideAktenErrorHandling(e);
        if (decision.logLevel === 'warn') {
          console.warn('[useAktenPaginated] Query tolerated as empty result', e);
        } else {
          console.error('[useAktenPaginated] Query failed', e);
          Sentry.captureException(e, { extra: { hook: 'useAktenPaginated', phase: 'loadFirstPage' } });
        }
        if (decision.treatAsEmpty) {
          setRows([]);
          setTotal(0);
          setNextOffset(0);
          setHasMore(false);
          setError(null);
        } else {
          setError(decision.userMessage);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadFirstPage();
    return () => { cancelled = true; };
  }, [applyFilters, pageSize, version, sortierung]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    (async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const fristResult = stableErweiterterFilter
          ? await preFetchFristAkteIds(stableErweiterterFilter, today)
          : null;

        let q = supabase.from('akten').select('*, creator_mitarbeiter:created_by_mitarbeiter_id(vorname, nachname)')
          .order(sortierung.column, { ascending: sortierung.ascending, nullsFirst: sortierung.nullsFirst })
          .range(nextOffset, nextOffset + pageSize - 1);
        q = applyFilters(q);
        q = applyFristFilter(q, fristResult);
        const { data, error: rowErr } = await q;
        if (rowErr) throw rowErr;
        const newRows = (data || []) as Akte[];
        setRows(prev => [...prev, ...newRows]);
        setNextOffset(prev => prev + newRows.length);
        setHasMore(newRows.length === pageSize && (nextOffset + newRows.length) < total);
      } catch (e: any) {
        const decision = decideAktenErrorHandling(e);
        if (decision.logLevel === 'warn') {
          console.warn('[useAktenPaginated.loadMore] Query tolerated as end-of-list', e);
        } else {
          console.error('[useAktenPaginated.loadMore] Query failed', e);
          Sentry.captureException(e, { extra: { hook: 'useAktenPaginated', phase: 'loadMore' } });
        }
        if (decision.treatAsEmpty) {
          setHasMore(false);
        } else {
          setError(decision.userMessage);
        }
      } finally {
        setLoadingMore(false);
      }
    })();
  }, [applyFilters, nextOffset, pageSize, hasMore, loadingMore, total, sortierung]);

  const refresh = useCallback(() => setVersion(v => v + 1), []);

  return { rows, total, loading, loadingMore, error, hasMore, loadMore, refresh };
}

/**
 * Server-side aggregate counts for the AktenPage StatistikLeiste. Runs four
 * parallel HEAD count queries so we don't need to stream 6,600+ rows just to
 * compute "5 new today". Scoped to a single business unit.
 */
export interface AktenStats {
  heuteNeu: number;
  dieseWoche: number;
  offenGesamt: number;
  abgeschlossenMonat: number;
}
export function useAktenStats(businessUnit?: BusinessUnit) {
  return useQuery(async (): Promise<AktenStats> => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const weekAgo = new Date(now.getTime() - 7 * 86400000).toISOString();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

    const scope = <T>(q: T): T => {
      const base = excludeDeleted(q);
      return (businessUnit ? (base as any).eq('business_unit', businessUnit) : base) as T;
    };

    const [heute, woche, offen, monatFertig] = await Promise.all([
      scope(supabase.from('akten').select('*', { count: 'exact', head: true }))
        .gte('created_at', todayStart),
      scope(supabase.from('akten').select('*', { count: 'exact', head: true }))
        .gte('created_at', weekAgo),
      scope(supabase.from('akten').select('*', { count: 'exact', head: true }))
        .not('status', 'in', '(abgeschlossen,storniert)'),
      scope(supabase.from('akten').select('*', { count: 'exact', head: true }))
        .eq('status', 'abgeschlossen')
        .gte('updated_at', monthStart),
    ]);

    return {
      heuteNeu: heute.count || 0,
      dieseWoche: woche.count || 0,
      offenGesamt: offen.count || 0,
      abgeschlossenMonat: monatFertig.count || 0,
    };
  }, [businessUnit], { heuteNeu: 0, dieseWoche: 0, offenGesamt: 0, abgeschlossenMonat: 0 });
}

export function useAkte(id: string | undefined) {
  return useQuery(async () => {
    if (!id) return null;
    const { data, error } = await supabase
      .from('akten')
      .select('*, creator_mitarbeiter:created_by_mitarbeiter_id(vorname, nachname)')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data as Akte;
  }, [id], null);
}

export function useAkteByNr(aktenNr: string | undefined) {
  return useQuery(async () => {
    if (!aktenNr) return null;
    // Match either akten_nummer (authoritative, NOT NULL) or akten_nr (nullable
    // legacy copy). Using .or() means we still find the row when one of the two
    // columns has drifted or is NULL — see issue #153.
    const identifier = aktenNr.trim();
    const { data, error } = await supabase
      .from('akten')
      .select('*, creator_mitarbeiter:created_by_mitarbeiter_id(vorname, nachname)')
      .or(`akten_nummer.eq.${identifier},akten_nr.eq.${identifier}`)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data as Akte | null;
  }, [aktenNr], null);
}

export function useAktenCounts() {
  return useQuery(async () => {
    const dataUnits: BusinessUnit[] = ['gutachten', 'autoglas', 'mietwagen', 'werkstatt'];
    const counts: Partial<Record<BetriebId, number>> = {};

    for (const id of BETRIEB_IDS) {
      counts[id] = 0;
    }

    for (const unit of dataUnits) {
      try {
        const { count, error } = await excludeDeleted(
          supabase
            .from('akten')
            .select('*', { count: 'exact', head: true })
            .eq('business_unit', unit)
            .neq('status', 'abgeschlossen')
            .neq('status', 'storniert'),
        );
        if (!error && count !== null) {
          counts[unit] = count;
        }
      } catch {
        // Tabelle existiert nicht → 0
      }
    }
    return counts as Record<BetriebId, number>;
  }, [], {} as Record<BetriebId, number>);
}

// ============================================================
// AUFGABEN
// ============================================================
export function useAufgaben(businessUnit?: BusinessUnit) {
  return useQuery(async () => {
    let query = supabase
      .from('aufgaben')
      .select('*, mitarbeiter:zugewiesen_an(vorname, nachname)')
      .order('faellig_am', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false });

    if (businessUnit) {
      query = query.eq('business_unit', businessUnit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as (Aufgabe & { mitarbeiter: { vorname: string; nachname: string } | null })[];
  }, [businessUnit], [] as any[]);
}

// ============================================================
// TERMINE
// ============================================================
export function useTermine(businessUnit?: BusinessUnit) {
  return useQuery(async () => {
    const withMitarbeiter = () => {
      let q = supabase
        .from('termine')
        .select('*, mitarbeiter:mitarbeiter_id(vorname, nachname)')
        .order('beginn', { ascending: true });
      if (businessUnit) q = q.eq('business_unit', businessUnit);
      return q;
    };
    const plain = () => {
      let q = supabase
        .from('termine')
        .select('*')
        .order('beginn', { ascending: true });
      if (businessUnit) q = q.eq('business_unit', businessUnit);
      return q;
    };

    const { data, error } = await withMitarbeiter();
    if (!error) {
      return data as (Termin & { mitarbeiter: { vorname: string; nachname: string } | null })[];
    }
    // ROBUST: Schlaegt der Embed fehl (z.B. fehlende FK / "schema cache"),
    // NICHT still leer zurueckgeben — Termine ohne Mitarbeiter-Namen laden,
    // damit der Kalender nie wieder unbemerkt leer ist.
    Sentry.captureException(error, { extra: { hook: 'useTermine', phase: 'embed-fallback' } });
    const { data: plainData, error: plainErr } = await plain();
    if (plainErr) throw plainErr;
    return (plainData as Termin[]).map(t => ({ ...t, mitarbeiter: null })) as (Termin & { mitarbeiter: { vorname: string; nachname: string } | null })[];
  }, [businessUnit], [] as any[]);
}

// ============================================================
// KUNDEN
// ============================================================
export function useKunden() {
  return useQuery(async () => {
    const { data, error } = await supabase
      .from('kunden')
      .select('*')
      .order('nachname', { ascending: true });
    if (error) throw error;
    return data as Kunde[];
  }, [], [] as any[]);
}

// ============================================================
// RECHNUNGEN
// ============================================================
export function useRechnungen(businessUnit?: BusinessUnit) {
  return useQuery(async () => {
    let query = supabase
      .from('rechnungen')
      .select('*, kunden(vorname, nachname, firma)')
      .order('rechnungsdatum', { ascending: false });

    if (businessUnit) {
      query = query.eq('business_unit', businessUnit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as (Rechnung & { kunden: { vorname: string; nachname: string; firma: string | null } | null })[];
  }, [businessUnit], [] as any[]);
}

export function useRechnung(id: string | undefined) {
  return useQuery(async () => {
    if (!id) return null;
    const { data, error } = await supabase
      .from('rechnungen')
      .select('*, kunden(vorname, nachname, firma, strasse, plz, ort)')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data as Rechnung & { kunden: any };
  }, [id], null);
}

export function useRechnungspositionen(rechnungId: string | undefined) {
  return useQuery(async () => {
    if (!rechnungId) return [];
    const { data, error } = await supabase
      .from('rechnungspositionen')
      .select('*')
      .eq('rechnung_id', rechnungId)
      .order('position_nr', { ascending: true });
    if (error) throw error;
    return data as Rechnungsposition[];
  }, [rechnungId], [] as Rechnungsposition[]);
}

// ============================================================
// MITARBEITER
// ============================================================
export function useMitarbeiter() {
  return useQuery(async () => {
    const { data, error } = await supabase
      .from('mitarbeiter')
      .select('*')
      .order('nachname', { ascending: true });
    if (error) throw error;
    return data as Mitarbeiter[];
  }, [], [] as any[]);
}

// ============================================================
// BETRIEBE (Firmendaten aus DB)
// ============================================================
export function useBetriebe() {
  return useQuery(async () => {
    const { data, error } = await supabase
      .from('betriebe')
      .select('*')
      .eq('aktiv', true)
      .order('name', { ascending: true });
    if (error) throw error;
    return data as BetriebDB[];
  }, [], [] as BetriebDB[]);
}

export function useBetrieb(typ: string | undefined) {
  return useQuery(async () => {
    if (!typ) return null;
    const { data, error } = await supabase
      .from('betriebe')
      .select('*')
      .eq('typ', typ)
      .single();
    if (error) throw error;
    return data as BetriebDB;
  }, [typ], null);
}

// ============================================================
// LEISTUNGS-KATALOG (Issue #369)
// ============================================================
export function useLeistungsKatalog(typ: string | undefined) {
  return useQuery(async () => {
    if (!typ) return [] as LeistungsKatalogItem[];
    const { data, error } = await supabase
      .from('leistungs_katalog')
      .select('*')
      .eq('typ', typ)
      .eq('ist_aktiv', true)
      .order('bezeichnung', { ascending: true });
    if (error) throw error;
    return (data || []) as LeistungsKatalogItem[];
  }, [typ], [] as LeistungsKatalogItem[]);
}

// ============================================================
// ZAHLUNGSEINGÄNGE
// ============================================================
export function useZahlungseingaenge(rechnungId?: string) {
  return useQuery(async () => {
    let query = supabase
      .from('zahlungseingaenge')
      .select('*')
      .order('datum', { ascending: false });

    if (rechnungId) {
      query = query.eq('rechnung_id', rechnungId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as Zahlungseingang[];
  }, [rechnungId], [] as Zahlungseingang[]);
}

// ============================================================
// DATEV EXPORTE
// ============================================================
export function useDatevExporte() {
  return useQuery(async () => {
    const { data, error } = await supabase
      .from('datev_exporte')
      .select('*')
      .order('export_datum', { ascending: false });
    if (error) throw error;
    return data as DatevExport[];
  }, [], [] as DatevExport[]);
}

// ============================================================
// HONORARTABELLE
// ============================================================
export function useHonorartabelle() {
  return useQuery(async () => {
    const { data, error } = await supabase
      .from('honorartabelle')
      .select('*')
      .eq('aktiv', true)
      .order('schadenshoehe_von', { ascending: true });
    if (error) throw error;
    return data as Honorar[];
  }, [], [] as Honorar[]);
}

// ============================================================
// DASHBOARD KPIs
// ============================================================
export function useDashboardKPIs(businessUnit: BusinessUnit) {
  return useQuery(async () => {
    let offeneAkten = 0;
    let abgeschlossen = 0;
    let offeneAufgaben = 0;
    let umsatz = 0;

    try {
      const { count } = await supabase
        .from('akten')
        .select('*', { count: 'exact', head: true })
        .eq('business_unit', businessUnit)
        .neq('status', 'abgeschlossen')
        .neq('status', 'storniert');
      offeneAkten = count || 0;
    } catch {}

    try {
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      const { count } = await supabase
        .from('akten')
        .select('*', { count: 'exact', head: true })
        .eq('business_unit', businessUnit)
        .eq('status', 'abgeschlossen')
        .gte('updated_at', startOfMonth.toISOString());
      abgeschlossen = count || 0;
    } catch {}

    try {
      const { count } = await supabase
        .from('aufgaben')
        .select('*', { count: 'exact', head: true })
        .eq('business_unit', businessUnit)
        .in('status', ['offen', 'in_bearbeitung']);
      offeneAufgaben = count || 0;
    } catch {}

    try {
      const { data: rechnungen } = await supabase
        .from('rechnungen')
        .select('brutto_betrag')
        .eq('business_unit', businessUnit)
        .eq('status', 'bezahlt');
      umsatz = (rechnungen as { brutto_betrag: number }[] | null)?.reduce((sum, r) => sum + (r.brutto_betrag || 0), 0) || 0;
    } catch {}

    return { offeneAkten, abgeschlossen, offeneAufgaben, umsatz };
  }, [businessUnit], { offeneAkten: 0, abgeschlossen: 0, offeneAufgaben: 0, umsatz: 0 });
}


// ============================================================
// SV-BÜRO: Gutachter-Tabellen
// ============================================================

/** Lade alle SV-Daten für eine Akte (Kontakte, Fahrzeug, Besichtigung, Bewertung, Kalkulation, Schadenspositionen) */
export function useSvAktenDaten(aktenId: string | null) {
  return useQuery(async () => {
    if (!aktenId) return null;
    const [kontakteRes, fahrzeugeRes, besichtigungenRes, bewertungenRes, kalkulationenRes, schadenposRes] = await Promise.all([
      supabase.from('kontakte').select('*').eq('akte_id', aktenId),
      supabase.from('fahrzeuge').select('*').eq('akte_id', aktenId),
      supabase.from('besichtigungen').select('*').eq('akte_id', aktenId),
      supabase.from('bewertungen').select('*').eq('akte_id', aktenId),
      supabase.from('kalkulationen').select('*').eq('akte_id', aktenId),
      supabase.from('schadenpositionen').select('*').eq('akte_id', aktenId).order('position_nr', { ascending: true }),
    ]);
    return {
      kontakte: (kontakteRes.data || []) as any[],
      fahrzeuge: (fahrzeugeRes.data || []) as any[],
      besichtigungen: (besichtigungenRes.data || []) as any[],
      bewertungen: (bewertungenRes.data || []) as any[],
      kalkulationen: (kalkulationenRes.data || []) as any[],
      schadenpositionen: (schadenposRes.data || []) as any[],
    };
  }, [aktenId], null);
}

/** Lade Textbausteine für den Gutachten-Editor */
export function useTextbausteine() {
  return useQuery(async () => {
    const { data, error } = await supabase
      .from('textbausteine')
      .select('*')
      .eq('aktiv', true)
      .order('kategorie', { ascending: true })
      .order('sortierung', { ascending: true });
    if (error) throw error;
    return data as any[];
  }, [], [] as any[]);
}

export interface NummernkreisMitDetails extends Nummernkreis {
  betrieb: { id: string; name: string; typ: string } | null;
  marke: { id: string; marken_name: string } | null;
}

/** Lade Nummernkreise mit Betrieb- und Marken-Details */
export function useNummernkreise() {
  return useQuery(async () => {
    const { data, error } = await supabase
      .from('nummernkreise')
      .select('*, betrieb:betriebe(id, name, typ), marke:marken(id, marken_name)')
      .order('praefix', { ascending: true });
    if (error) throw error;
    return (data ?? []) as NummernkreisMitDetails[];
  }, [], [] as NummernkreisMitDetails[]);
}

// ─── useAktenFristenMin (Issue #652) ─────────────────────────────────────────

/**
 * Lädt die früheste offene Frist pro Akte für eine Liste von Akten-IDs.
 * Ergebnis: Map von akte_id → ISO-Datum-String (frühestes faellig_am).
 * Leere Map wenn keine Fristen vorhanden oder Tabelle fehlt.
 */
export function useAktenFristenMin(akteIds: string[]): Map<string, string> {
  const [result, setResult] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (akteIds.length === 0) {
      setResult(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('akten_fristen')
          .select('akte_id, faellig_am')
          .in('akte_id', akteIds)
          .is('erledigt_am', null)
          .order('faellig_am', { ascending: true });
        if (error) throw error;
        if (cancelled) return;
        const map = new Map<string, string>();
        for (const row of (data ?? []) as Pick<AkteFrist, 'akte_id' | 'faellig_am'>[]) {
          if (!map.has(row.akte_id)) {
            map.set(row.akte_id, row.faellig_am);
          }
        }
        setResult(map);
      } catch {
        // Tabelle noch nicht migriert oder RLS — stille Degradation
        if (!cancelled) setResult(new Map());
      }
    })();
    return () => { cancelled = true; };
  }, [akteIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  return result;
}
